import { useState, useEffect } from "react";
import Navbar from "./Navbar";
import UploadModal from "./UploadModal";
import Filters from "./Filters";
import axios from "axios";
import { BACKEND_URL } from "../data/config";

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [absents, setAbsents] = useState([]);
  const [filteredAbsents, setFilteredAbsents] = useState([]);

  const [filters, setFilters] = useState({ month: "" });

  const [searchName, setSearchName] = useState("");
  const [searchDate, setSearchDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const loadData = async (month = filters.month) => {
    setLoading(true);
    try {
      const [statsRes, absentsRes] = await Promise.all([
        axios.get(
          `${BACKEND_URL}/api/dashboard-stats.php?month=${month}`
        ),
        axios.get(
          `${BACKEND_URL}/api/top-absents.php?month=${month}`
        ),
      ]);

      setStats(statsRes.data.stats);
      setAbsents(absentsRes.data.data);
      setFilteredAbsents(absentsRes.data.data);
    } catch (error) {
      console.error("Loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    setFilters({ month: currentMonth });
  }, []);

  useEffect(() => {
    if (filters.month) loadData(filters.month);
  }, [filters.month]);

  const handleFilterChange = (month) => {
    setFilters({ month });
  };

  /*
  SEARCH FILTER
  */
  useEffect(() => {
    let filtered = absents;

    if (searchName) {
      filtered = filtered.filter((a) =>
        a.name.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (searchDate) {
      filtered = filtered.filter(
        (a) => a.date.slice(0, 10) === searchDate
      );
    }

    setFilteredAbsents(filtered);
    setCurrentPage(1);
  }, [searchName, searchDate, absents]);

  /*
  PAGINATION
  */

  const indexLast = currentPage * rowsPerPage;
  const indexFirst = indexLast - rowsPerPage;
  const currentRows = filteredAbsents.slice(indexFirst, indexLast);

  const totalPages = Math.ceil(filteredAbsents.length / rowsPerPage);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      <div className="max-w-6xl mx-auto p-6">

        {/* IMPORT */}

        <div className="bg-white border rounded-lg p-6 mt-6 mb-8 flex justify-between items-center">

          <div>
            <h3 className="font-semibold">
              Import Attendance File
            </h3>
            <p className="text-sm text-gray-500">
              Upload CSV attendance data
            </p>
          </div>

          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700"
          >
            Upload CSV
          </button>

        </div>

        <Filters onFilterChange={handleFilterChange} />

        {/* STATS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 mb-8">

          <div className="bg-white p-5 rounded-lg border">
            <p className="text-sm text-gray-500">Employees</p>
            <p className="text-2xl font-semibold">
              {stats.totalEmployees || 0}
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg border">
            <p className="text-sm text-gray-500">Late Arrivals</p>
            <p className="text-2xl font-semibold text-orange-600">
              {stats.lateCount || 0}
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg border">
            <p className="text-sm text-gray-500">Early Departures</p>
            <p className="text-2xl font-semibold text-red-500">
              {stats.earlyCount || 0}
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg border">
            <p className="text-sm text-gray-500">Absences</p>
            <p className="text-2xl font-semibold text-gray-700">
              {stats.absentCount || 0}
            </p>
          </div>

        </div>

        {/* ABSENTS LIST */}

        <div className="bg-white border rounded-lg p-6">

          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-lg">
              Absences - {filters.month}
            </h2>
          </div>

          {/* SEARCH */}

          <div className="flex gap-4 mb-6">

            <input
              type="text"
              placeholder="Search employee..."
              className="border rounded-md px-3 py-2 w-60"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />

            <input
              type="date"
              className="border rounded-md px-3 py-2"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
            />

          </div>

          {/* TABLE */}

          <div className="overflow-x-auto">

            <table className="w-full text-sm">

              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">#</th>
                  <th className="text-left p-3">Employee</th>
                  <th className="text-left p-3">Absence Days</th>
                  <th className="text-left p-3">Date</th>
                </tr>
              </thead>

              <tbody>

                {currentRows.length ? (
                  currentRows.map((absent, index) => (
                    <tr key={index} className="border-t">

                      <td className="p-3">
                        {indexFirst + index + 1}
                      </td>

                      <td className="p-3 font-medium">
                        {absent.name}
                      </td>

                      <td className="p-3 text-red-600">
                        {absent.absent_days}
                      </td>

                      <td className="p-3">
                        {new Date(absent.date).toLocaleDateString()}
                      </td>

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center p-6 text-gray-400">
                      No absences found
                    </td>
                  </tr>
                )}

              </tbody>

            </table>

          </div>

          {/* PAGINATION */}

          <div className="flex justify-center mt-6 gap-2">

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-white"
                }`}
              >
                {i + 1}
              </button>
            ))}

          </div>

        </div>

      </div>

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      />

    </div>
  );
};

export default Dashboard;